import Button from "@irati/ui/src/Button"
import React, {useEffect, useMemo} from "react"
import {FormProvider, useForm, UseFormReturn} from "react-hook-form"
import {CheckoutFormFields} from "./CheckoutFormFields"
import {yupResolver} from "@hookform/resolvers/yup"
import Spacing from "@irati/ui/src/Spacing"
import {validationSchema, defaultFormValues} from "./formData"
import {useCheckoutDataQuery} from "@irati/payrollpay/hooks"
import {useStoreContext} from "../../context/StoreContext"
import Loading from "@irati/ui/src/Loading"

const transformCheckoutDataToForm = (data?: any): Partial<FormData> => {
  if (!data) return {}

  const user = data.currentUser ?? ({} as any)
  const employee = data.employeeOrganisation?.employee ?? ({} as any)

  const dob = user.dateOfBirth?.split("-") ?? []
  const [year, monthRaw, dayRaw] = dob.length === 3 ? dob : ["", "", ""]
  const month = monthRaw ? String(parseInt(monthRaw, 10)) : ""
  const day = dayRaw ? String(parseInt(dayRaw, 10)) : ""

  return {
    placeOfWork: user.placeOfWork ?? "",
    employeeNumber: employee.employeeNumber ?? "",
    niNumber: user.niNumber ?? "",
    isTemporaryNI: false,
    title: (user.title ?? "").toString().toLowerCase(),
    firstName: user.firstName ?? "",
    lastName: user.lastName ?? "",
    dobDay: day,
    dobMonth: month,
    dobYear: year,
    email: user.email ?? "",
    mobileNumber: user.mobileNumber ?? "",
    addressLine1: user.address1 ?? "",
    addressLine2: user.address2 ?? "",
    town: user.town ?? "",
    county: user.county ?? "",
    postcode: user.postcode ?? ""
  }
}

export interface FormData {
  placeOfWork: string
  employeeNumber: string
  niNumber: string
  title: string
  firstName: string
  lastName: string
  isTemporaryNI: boolean
  dobDay: string
  dobMonth: string
  dobYear: string
  email: string
  mobileNumber: string
  addressLine1: string
  addressLine2: string
  town: string
  county: string
  postcode: string
}

interface CheckoutFormProps {
  onSubmit: (data: FormData) => void
  defaultValues?: Partial<FormData>
  methods?: UseFormReturn<FormData>
}

export const CheckoutForm: React.FC<CheckoutFormProps> = ({onSubmit, defaultValues, methods: externalMethods}) => {
  const {schemeType} = useStoreContext()

  const {data: checkoutData, load} = useCheckoutDataQuery({schemeType})

  const initialDefaults = useMemo(
    () => ({
      ...defaultFormValues,
      ...defaultValues
    }),
    [defaultValues]
  )

  const methods =
    externalMethods ||
    useForm<FormData>({
      resolver: yupResolver(validationSchema),
      defaultValues: initialDefaults as FormData,
      mode: "onChange"
    })

  useEffect(() => {
    if (checkoutData) {
      const transformed = transformCheckoutDataToForm(checkoutData)
      methods.reset({
        ...defaultFormValues,
        ...transformed
      })
    }
  }, [checkoutData, methods])

  return (
    <Loading load={load}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <CheckoutFormFields />
          <Spacing padding={3}>
            <Button mobileFull type="submit">
              Submit
            </Button>
          </Spacing>
        </form>
      </FormProvider>
    </Loading>
  )
}
